import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  FiChevronLeft, FiChevronRight, FiCheck, FiPhone, FiMail, 
  FiTruck, FiShield, FiHome, FiPackage, FiTool, FiZap,
  FiDroplet, FiLayers, FiSquare, FiGrid, FiShare2, FiHeart, FiMapPin
} from 'react-icons/fi';
import { shedService } from '../services/shedService';
import { stripeConnectService } from '../services/stripeConnectService';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import LeadFormShed from '../components/LeadFormShed';
import StripeCheckout from '../components/StripeCheckout';
import PurchaseAgreement from '../components/PurchaseAgreement';

class ShedDetailErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('ShedDetail render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 pt-24 flex flex-col items-center justify-center px-4">
          <FiHome className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Something went wrong</h2>
          <p className="text-gray-500 mb-6 text-center">We had trouble displaying this shed. Try refreshing or browse other listings.</p>
          <div className="flex gap-3">
            <button onClick={() => window.location.reload()} className="bg-gray-200 text-gray-700 px-6 py-3 text-sm font-medium hover:bg-gray-300 transition-colors">
              Refresh Page
            </button>
            <a href="/sheds" className="bg-gray-900 text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors">
              Browse All Sheds
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ShedDetail = () => {
  const { id } = useParams();
  const [shed, setShed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [inquiryType, setInquiryType] = useState('general');
  const { toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart, isInCart } = useCart();
  const [activeTab, setActiveTab] = useState('overview');
  const [dealer, setDealer] = useState(null);
  const [stripePaymentType, setStripePaymentType] = useState('deposit');
  const [showAgreement, setShowAgreement] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);

  useEffect(() => {
    loadShed();
  }, [id]);

  const loadShed = async () => {
    setLoading(true);
    try {
      const { data, error } = await shedService.getShedById(id);
      if (!error && data) {
        setShed(data);
        // Load dealer info for Stripe Connect payments
        if (data.dealer_id || data.dealerId) {
          const dealerResult = await stripeConnectService.getDealerForShed(data.id || id);
          if (dealerResult.data) {
            setDealer(dealerResult.data);
          }
        }
      }
    } catch (err) {
      console.error('Error loading shed:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleInquiry = (type) => {
    setInquiryType(type);
    setShowLeadForm(true);
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = shed?.name || 'Check out this shed';
    const shareText = `${shareTitle} - ${shed?.size} ${shed?.shedType} for ${formatPrice(shed?.salePrice || shed?.price)}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Link copied to clipboard!');
      }
    }
  };

  const nextImage = () => {
    if (shed?.images?.length > 1) {
      setSelectedImage((prev) => (prev + 1) % shed.images.length);
    }
  };

  const prevImage = () => {
    if (shed?.images?.length > 1) {
      setSelectedImage((prev) => (prev - 1 + shed.images.length) % shed.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!shed) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex flex-col items-center justify-center">
        <FiHome className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Shed Not Found</h2>
        <p className="text-gray-500 mb-6">The shed you're looking for doesn't exist or has been sold.</p>
        <Link to="/sheds" className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700">
          Browse All Sheds
        </Link>
      </div>
    );
  }

  const displayPrice = shed.salePrice || shed.price;
  const hasDiscount = shed.salePrice && shed.salePrice < shed.price;

  const getRtoMonthly = () => {
    const base = hasDiscount ? shed.salePrice : shed.price;
    const monthly = Math.round((base * 1.45) / 36);
    return formatPrice(monthly);
  };

  return (
    <>
      <Helmet>
        <title>{shed.name} | {shed.size} {shed.shedType} Shed</title>
        <meta name="description" content={`${shed.name} - ${shed.size} ${shed.material} ${shed.shedType} shed. ${shed.description?.substring(0, 150)}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-emerald-600">Home</Link>
              <span className="text-gray-300">/</span>
              <Link to="/sheds" className="text-gray-500 hover:text-emerald-600">Sheds</Link>
              <span className="text-gray-300">/</span>
              <span className="text-gray-900">{shed.name}</span>
            </nav>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Images & Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Main Image */}
              <div className="bg-white border border-gray-200 overflow-hidden">
                <div className="relative aspect-[16/10]">
                  <img
                    src={shed.images?.[selectedImage] || '/assets/images/placeholder-shed.jpg'}
                    alt={shed.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Navigation Arrows */}
                  {shed.images?.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2.5 transition-colors"
                      >
                        <FiChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2.5 transition-colors"
                      >
                        <FiChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    {hasDiscount && (
                      <span className="bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">
                        Sale
                      </span>
                    )}
                    {shed.deliveryIncluded && (
                      <span className="bg-gray-900 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">
                        Free Delivery
                      </span>
                    )}
                  </div>

                  {/* Image Counter */}
                  {shed.images?.length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black/40 text-white text-xs px-2.5 py-1">
                      {selectedImage + 1} / {shed.images.length}
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {shed.images?.length > 1 && (
                  <div className="p-4 flex gap-2 overflow-x-auto">
                    {shed.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 transition-colors ${
                          selectedImage === idx ? 'border-gray-900' : 'border-transparent'
                        }`}
                      >
                        <img src={img} alt={`${shed.name} ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="bg-white border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200">
                  <div className="flex">
                    {['overview', 'specifications', 'features', 'warranty'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`flex-1 px-6 py-3.5 text-sm capitalize transition-colors ${
                          activeTab === tab
                            ? 'text-gray-900 font-medium border-b-2 border-gray-900'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-6">
                  {activeTab === 'overview' && (
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">About This Shed</h2>
                      <p className="text-gray-600 leading-relaxed mb-6">{shed.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
                        <div className="bg-white p-4 text-center">
                          <div className="text-lg font-semibold text-gray-900">{shed.size}</div>
                          <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Dimensions</div>
                        </div>
                        <div className="bg-white p-4 text-center">
                          <div className="text-lg font-semibold text-gray-900">{shed.squareFeet}</div>
                          <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Sq Feet</div>
                        </div>
                        <div className="bg-white p-4 text-center">
                          <div className="text-lg font-semibold text-gray-900 capitalize">{shed.material}</div>
                          <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Material</div>
                        </div>
                        <div className="bg-white p-4 text-center">
                          <div className="text-lg font-semibold text-gray-900">{shed.warrantyYears} Yr</div>
                          <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Warranty</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'specifications' && (
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">Specifications</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Size</span>
                            <span className="font-medium">{shed.size}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Width</span>
                            <span className="font-medium">{shed.width} ft</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Depth</span>
                            <span className="font-medium">{shed.depth} ft</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Height</span>
                            <span className="font-medium">{shed.height} ft</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Square Feet</span>
                            <span className="font-medium">{shed.squareFeet} sq ft</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Year Built</span>
                            <span className="font-medium">{shed.yearBuilt}</span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Material</span>
                            <span className="font-medium">{shed.material}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Roof Style</span>
                            <span className="font-medium">{shed.roofStyle}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Roof Material</span>
                            <span className="font-medium">{shed.roofMaterial}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Siding</span>
                            <span className="font-medium">{shed.sidingType}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Floor</span>
                            <span className="font-medium">{shed.floorType}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Foundation</span>
                            <span className="font-medium">{shed.foundation}</span>
                          </div>
                        </div>
                      </div>

                      {/* Colors */}
                      <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Colors</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-500">Siding Color</span>
                          <span className="font-medium">{shed.sidingColor}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-500">Trim Color</span>
                          <span className="font-medium">{shed.trimColor}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-500">Roof Color</span>
                          <span className="font-medium">{shed.roofColor}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">Features & Upgrades</h2>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 border border-gray-200 mb-8">
                        {[
                          { label: 'Doors', value: shed.doors || 'Standard', included: !!shed.doors },
                          { label: 'Windows', value: shed.windows || 'None', included: !!shed.windows },
                          { label: 'Loft', value: shed.loft ? 'Included' : 'Not included', included: shed.loft },
                          { label: 'Workbench', value: shed.workbench ? 'Included' : 'Not included', included: shed.workbench },
                          { label: 'Electrical', value: shed.electrical ? 'Pre-wired' : 'Not included', included: shed.electrical },
                          { label: 'Insulation', value: shed.insulation ? 'Insulated' : 'Not included', included: shed.insulation },
                        ].map((feature) => (
                          <div key={feature.label} className="bg-white p-4 flex items-center justify-between">
                            <span className="text-sm text-gray-900">{feature.label}</span>
                            <span className={`text-sm ${feature.included ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                              {feature.value}
                            </span>
                          </div>
                        ))}
                      </div>

                      {shed.features?.length > 0 && (
                        <>
                          <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3">Additional features</h3>
                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                            {shed.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'warranty' && (
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">Warranty Information</h2>
                      <div className="border border-gray-200 p-5 mb-6">
                        <div className="text-xl font-semibold text-gray-900">{shed.warranty}</div>
                        <div className="text-sm text-gray-500 mt-1">Manufacturer warranty</div>
                      </div>
                      <div className="prose text-gray-600">
                        <p>This shed comes with a {shed.warrantyYears}-year limited warranty covering:</p>
                        <ul>
                          <li>Structural defects in materials and workmanship</li>
                          <li>Roof leaks under normal conditions</li>
                          <li>Siding defects and premature fading</li>
                          <li>Door and window hardware</li>
                        </ul>
                        <p className="text-sm text-gray-500 mt-4">
                          Warranty does not cover damage from improper installation, natural disasters, 
                          or normal wear and tear. Contact us for full warranty details.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Pricing & Actions */}
            <div className="space-y-6">
              {/* Price Card */}
              <div className="bg-white border border-gray-200 p-6 sticky top-20">
                {/* Status + Wishlist */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`flex items-center gap-1.5 text-xs font-medium ${shed.status === 'available' ? 'text-green-600' : shed.status === 'reserved' ? 'text-amber-600' : 'text-gray-400'}`}>
                    <span className={`w-2 h-2 rounded-full ${shed.status === 'available' ? 'bg-green-500' : shed.status === 'reserved' ? 'bg-amber-500' : 'bg-gray-400'}`} />
                    {shed.status === 'available' ? 'Available at Lot' : shed.status === 'reserved' ? 'Reserved' : shed.status}
                  </span>
                  <button
                    onClick={() => toggleWishlist(shed.id)}
                    className="p-1.5 hover:bg-gray-50 transition-colors"
                    title={isWishlisted(shed.id) ? 'Remove from saved' : 'Save'}
                  >
                    <FiHeart className={`w-5 h-5 ${isWishlisted(shed.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-xs text-gray-400 uppercase tracking-wider capitalize">{shed.brand} {shed.shedType}</p>
                  <h1 className="text-xl font-semibold text-gray-900 mt-1">{shed.name}</h1>
                  {shed.dealerName && (
                    <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                      <FiMapPin className="w-3 h-3" />
                      {shed.dealerName}{shed.dealerLocation ? ` — ${shed.dealerLocation}` : ''}
                    </p>
                  )}
                </div>

                {/* Price */}
                <div className="mb-6 pb-6 border-b border-gray-100">
                  {hasDiscount ? (
                    <div>
                      <span className="text-lg text-gray-400 line-through block">
                        {formatPrice(shed.price)}
                      </span>
                      <span className="text-3xl font-semibold text-red-600">
                        {formatPrice(shed.salePrice)}
                      </span>
                      <span className="inline-block mt-1 text-xs text-red-600 font-medium">
                        Save {formatPrice(shed.price - shed.salePrice)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-3xl font-semibold text-gray-900">
                      {formatPrice(shed.price)}
                    </span>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    or <span className="font-medium text-gray-700">{getRtoMonthly()}/mo</span> with Rent-to-Own (36 mo)
                  </p>
                </div>

                {/* Key Info */}
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Size</span>
                    <span className="font-medium text-gray-900">{shed.size} &middot; {shed.squareFeet} sq ft</span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Style</span>
                    <span className="font-medium text-gray-900 capitalize">{shed.shedType} &middot; {shed.material}</span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Warranty</span>
                    <span className="font-medium text-gray-900">{shed.warranty}</span>
                  </div>
                  {shed.deliveryIncluded && (
                    <div className="flex items-center justify-between text-gray-600">
                      <span>Delivery</span>
                      <span className="font-medium text-gray-900">Free</span>
                    </div>
                  )}
                </div>

                {/* Stock Number */}
                {shed.stockNumber && (
                  <div className="text-sm text-gray-500 mb-6">
                    Stock #: {shed.stockNumber} | Serial: {shed.serialNumber}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-2.5">
                  {isInCart(shed.id) ? (
                    <Link
                      to="/cart"
                      className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white text-sm font-medium py-3.5 transition-colors hover:bg-emerald-700"
                    >
                      <FiCheck className="w-4 h-4" />
                      View in Cart
                    </Link>
                  ) : (
                    <button
                      onClick={() => addToCart(shed)}
                      className="w-full bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium py-3.5 transition-colors"
                    >
                      Add to Cart — {formatPrice(shed.depositAmount || Math.round((shed.salePrice || shed.price) * 0.25))} deposit
                    </button>
                  )}
                  <button
                    onClick={() => { setStripePaymentType('deposit'); setShowAgreement(true); }}
                    className="w-full border border-gray-300 text-gray-700 hover:border-gray-400 text-sm font-medium py-3.5 transition-colors"
                  >
                    Reserve with deposit now
                  </button>
                  <button
                    onClick={() => handleInquiry('quote')}
                    className="w-full border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 text-sm font-medium py-3.5 transition-colors"
                  >
                    Request a quote
                  </button>
                </div>

                {/* Color Specs */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-2">Colors</p>
                  <div className="flex gap-4 text-xs">
                    <div>
                      <span className="text-gray-400 block">Body</span>
                      <span className="text-gray-700">{shed.bodyColor || shed.sidingColor || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">Roof</span>
                      <span className="text-gray-700">{shed.roofColor || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">Trim</span>
                      <span className="text-gray-700">{shed.trimColor || '—'}</span>
                    </div>
                  </div>
                </div>

                {/* Builder */}
                {shed.brand && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] text-gray-400 uppercase tracking-wider">Built by</span>
                    <span className="text-sm font-medium text-gray-900">{shed.brand}</span>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <a href="tel:+1" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                    Call us
                  </a>
                  <button onClick={handleShare} className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                    Share
                  </button>
                  <button onClick={() => handleInquiry('general')} className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                    Email
                  </button>
                </div>
              </div>

              {/* Delivery Info */}
              <div className="bg-white border border-gray-200 p-6">
                <h3 className="text-xs uppercase tracking-wider text-gray-400 mb-3">Delivery &amp; Setup</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  {shed.deliveryIncluded ? (
                    <div className="flex items-center gap-2">
                      <FiTruck className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span><span className="font-medium text-gray-900">Free delivery</span> within 50 miles</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FiTruck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>Delivery available — contact for quote</span>
                    </div>
                  )}
                  {shed.setupIncluded && (
                    <div className="flex items-center gap-2">
                      <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span><span className="font-medium text-gray-900">Free setup</span> on your prepared site</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 pt-1">Estimated lead time: 5–10 business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Form Modal */}
      <LeadFormShed
        isOpen={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        shed={shed}
        inquiryType={inquiryType}
      />

      {/* Purchase Agreement Modal */}
      <PurchaseAgreement
        isOpen={showAgreement}
        onClose={() => setShowAgreement(false)}
        onComplete={(agreementData) => {
          setShowAgreement(false);
          setCustomerInfo(agreementData);
          setShowPaymentModal(true);
        }}
        shed={shed}
        dealer={dealer}
        customer={customerInfo}
        paymentType={stripePaymentType}
      />

      {/* Stripe Checkout Modal */}
      {showPaymentModal && (
        <StripeCheckout
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          shed={shed}
          dealer={dealer}
          paymentType={stripePaymentType}
          customerInfo={customerInfo}
        />
      )}
    </>
  );
};

const ShedDetailWithBoundary = (props) => (
  <ShedDetailErrorBoundary>
    <ShedDetail {...props} />
  </ShedDetailErrorBoundary>
);

export default ShedDetailWithBoundary;
